/* eslint-disable class-methods-use-this */
import { Dispatch, SetStateAction } from 'react'
import { LatLngTuple } from 'leaflet'
import Utils from 'utils'

export interface SyncFloorMapParams {
  imagePath?: string
  isUnregisteredMap?: boolean
  mapInstance?: any
  setImgBounds: Dispatch<SetStateAction<LatLngTuple>>
  setImageLoading?: Dispatch<SetStateAction<boolean | undefined>>
  maxBoundsPadding?: { x: number; y: number }
  onErrorFallback?: () => Promise<string | undefined>
}

/**
 * FloorMapSyncHandler
 * Domain handler responsible for calculating floor map bounds, zoom levels,
 * and synchronizing Leaflet map instances upon image changes.
 */
export class FloorMapSyncHandler {
  /**
   * Helper to resolve a map instance from ref, function, or direct object
   */
  public static resolveMapInstance(mapInstanceOrRef: any): any {
    if (!mapInstanceOrRef) return null
    if (typeof mapInstanceOrRef === 'function') return mapInstanceOrRef()
    if ('current' in mapInstanceOrRef) return mapInstanceOrRef.current
    return mapInstanceOrRef
  }

  /**
   * Initializes and fits the Leaflet map bounds and zoom levels based on image dimensions
   */
  public static initMapBounds(
    mapInstance: any,
    setImgBounds: Dispatch<SetStateAction<LatLngTuple>>,
    img?: HTMLImageElement,
    isUnregisteredMap?: boolean,
    maxBoundsPadding?: { x: number; y: number }
  ): void {
    const resolvedMap = FloorMapSyncHandler.resolveMapInstance(mapInstance)
    if (!resolvedMap) return

    const height = isUnregisteredMap
      ? window.innerHeight
      : img?.naturalHeight || 0
    const width = isUnregisteredMap ? window.innerWidth : img?.naturalWidth || 0

    if (!height || !width) return

    setImgBounds([height, width])

    const initBound: LatLngTuple[] = [
      [0, 0],
      [height, width]
    ]

    resolvedMap.setMinZoom?.(-999)
    resolvedMap.fitBounds?.(initBound, { animate: false })

    if (maxBoundsPadding) {
      resolvedMap.setMaxBounds?.([
        [-maxBoundsPadding.y, -maxBoundsPadding.x],
        [height + maxBoundsPadding.y, width + maxBoundsPadding.x]
      ])
    } else {
      resolvedMap.setMaxBounds?.(initBound)
    }

    const minZoomLevel = resolvedMap.getBoundsZoom?.(initBound)
    if (typeof minZoomLevel === 'number') {
      resolvedMap.setView?.([height / 2, width / 2], minZoomLevel, {
        animate: false
      })
      resolvedMap.setMinZoom?.(minZoomLevel)
      resolvedMap.setMaxZoom?.(minZoomLevel + 5)
    }
  }

  /**
   * Synchronizes map bounds and view when floor image or registration changes
   */
  public static async handleSyncMapOnImageChange({
    imagePath,
    isUnregisteredMap,
    mapInstance,
    setImgBounds,
    setImageLoading,
    maxBoundsPadding,
    onErrorFallback
  }: SyncFloorMapParams): Promise<void> {
    if (!imagePath) return

    setImageLoading?.(true)

    try {
      const img = await Utils.getMeta(imagePath)
      const resolved = FloorMapSyncHandler.resolveMapInstance(mapInstance)
      if (resolved) {
        FloorMapSyncHandler.initMapBounds(
          resolved,
          setImgBounds,
          img,
          isUnregisteredMap,
          maxBoundsPadding
        )
      }
      setImageLoading?.(false)
    } catch {
      if (onErrorFallback) {
        try {
          const fallbackPath = await onErrorFallback()
          if (fallbackPath) {
            const img = await Utils.getMeta(fallbackPath)
            const resolved = FloorMapSyncHandler.resolveMapInstance(mapInstance)
            if (resolved) {
              FloorMapSyncHandler.initMapBounds(
                resolved,
                setImgBounds,
                img,
                isUnregisteredMap,
                maxBoundsPadding
              )
            }
          }
        } catch {
          // Handled by fallback caller
        }
      }
      setImageLoading?.(false)
    }
  }

  public handleSyncMapOnImageChange = (
    params: SyncFloorMapParams
  ): Promise<void> => {
    return FloorMapSyncHandler.handleSyncMapOnImageChange(params)
  }

  public initMapBounds = (
    mapInstance: any,
    setImgBounds: Dispatch<SetStateAction<LatLngTuple>>,
    img?: HTMLImageElement,
    isUnregisteredMap?: boolean,
    maxBoundsPadding?: { x: number; y: number }
  ): void => {
    FloorMapSyncHandler.initMapBounds(
      mapInstance,
      setImgBounds,
      img,
      isUnregisteredMap,
      maxBoundsPadding
    )
  }
}
